import { CartUpdateEvent } from '@theme/events';
import { collectCartSectionIds } from '@theme/cart-sections';

/**
 * "Save 10% on 3 Month Delivery" upgrade control, one per upgradeable cart line.
 *
 * On click it swaps a 30-day subscription line for the corresponding 90-day
 * sibling variant on the same product. The 30-day and 90-day plans are separate
 * variants (each with its own SKU and selling plan), so the upgrade is a
 * remove + add rather than a single cart/change call.
 *
 * Scoped to its own row: the `upgrading` guard and the click listener live on
 * the element, so a click on one line's upgrade no longer blocks an unrelated
 * line's button (the former document-delegated handler shared one global flag).
 *
 * Markup (snippets/cart-products.liquid):
 *   <cart-subscription-upgrade class="cart-items__subscription-upsell">
 *     <span>…</span>
 *     <button data-upgrade-subscription data-line data-line-key
 *             data-variant-id data-plan-id data-quantity>Upgrade</button>
 *   </cart-subscription-upgrade>
 */
class CartSubscriptionUpgrade extends HTMLElement {
  /**
   * Guards against a second upgrade starting while one is in flight on this
   * line. The button is re-rendered (and its `disabled` reset) by the cart
   * morph, so the button's own disabled state is not a reliable lock.
   * @type {boolean}
   */
  #upgrading = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * Run a cart mutation serialized against every other cart handler, falling
   * back to immediate execution if the shared lock has not loaded.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  #runLocked(task) {
    return window.AdegenCartLock ? window.AdegenCartLock.run(task) : task();
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-upgrade-subscription]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (this.#upgrading) return;

    const line = Number(button.dataset.line || 0);
    const lineKey = button.dataset.lineKey || '';
    const variantId = Number(button.dataset.variantId || 0);
    const planId = Number(button.dataset.planId || 0);
    // Carry over the line's existing quantity so upgrading a line with qty > 1
    // keeps that quantity instead of resetting to 1.
    const quantity = Math.max(1, Number(button.dataset.quantity || 1));
    if (!line || !variantId || !planId) return;

    this.#upgrading = true;
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Updating...';

    try {
      // Serialize the whole remove+add against every other cart mutation so a
      // stale concurrent response can't morph the cart on top of the upgraded
      // line.
      const data = await this.#runLocked(async () => {
        const sectionsList = collectCartSectionIds();
        // Add the 90-day sibling FIRST, then remove the 30-day line. If the add
        // fails (sold out between render and click, 422), nothing has been
        // removed yet, so the cart keeps the original line instead of ending up
        // empty of the product. The remove re-renders the sections for the
        // final cart state.
        await this.#addUpgradedVariant({ variantId, planId, quantity, sectionsList });
        return this.#removeLine({ line, lineKey, sectionsList });
      });

      document.dispatchEvent(
        new CartUpdateEvent(data, 'cart-drawer-subscription-upsell', {
          source: 'cart-drawer-subscription-upsell',
          itemCount: data.item_count || 0,
          sections: data.sections,
        })
      );
    } catch (_) {
      button.textContent = originalText || 'Upgrade';
      button.disabled = false;
      this.#upgrading = false;
      return;
    }

    button.textContent = 'Upgraded';
    this.#upgrading = false;
    setTimeout(() => {
      button.textContent = originalText || 'Upgrade';
      button.disabled = false;
    }, 1200);
  };

  /**
   * Remove the existing 30-day line via cart/change.js. Prefers the line key
   * (stable across cart edits) and falls back to the line index.
   *
   * @param {{ line: number, lineKey: string, sectionsList?: string }} args
   */
  async #removeLine({ line, lineKey, sectionsList }) {
    const url = window.Theme?.routes?.cart_change_url || '/cart/change.js';
    const body = lineKey ? { id: lineKey, quantity: 0 } : { line, quantity: 0 };
    // Ask for the same sections the add collected so the final cart state
    // (post-remove) is what re-renders, not the intermediate 30+90 state.
    if (sectionsList) {
      body.sections = sectionsList;
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error('upgrade-remove-failed');
    }
    return response.json();
  }

  /**
   * Add the 90-day sibling variant (with its attached selling plan) to the cart
   * at the same quantity as the removed line via cart/add.js. Returns the cart
   * response so the drawer can re-render.
   *
   * @param {{ variantId: number, planId: number, quantity: number, sectionsList: string }} args
   */
  async #addUpgradedVariant({ variantId, planId, quantity, sectionsList }) {
    const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: quantity || 1,
          selling_plan: planId,
        },
      ],
      sections: sectionsList,
      sections_url: window.location.pathname,
    });
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });
    const responseText = await response.text();
    const data = JSON.parse(responseText);
    if (!response.ok || data.errors) {
      throw new Error('upgrade-add-failed');
    }
    return data;
  }
}

if (!customElements.get('cart-subscription-upgrade')) {
  customElements.define('cart-subscription-upgrade', CartSubscriptionUpgrade);
}
